import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { listAllEmployees, peopleGateway } from '../../people';
import type { EmployeeResponse } from '../../people/types';
import { timeAndLeaveGateway } from '../index';
import type {
  EscoHrEventResponse, EscoHrEventStatus, EscoHrMappingResponse, EscoHrSigningSecretResponse, EscoHrStatusResponse,
} from '../types';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { RetryableError } from '../../../shared/RetryableError';
import { usePermissionCheck } from '../../identity/state/usePermissions';
import { EscoHrAttendanceSection } from './EscoHrAttendanceSection';
import {
  displayStatusLabels, formatWhen, LOCKED_MESSAGE, LOCKED_TITLE, lockedReason, PLAN_REASONS, statusNote,
} from '../escoHrCopy';

/** Same codes the server checks (EPN.Identity.Contracts PermissionCodes). Used only to hide or disable a control. */
const INTEGRATIONS_READ = 'integrations.read';
const INTEGRATIONS_MANAGE = 'integrations.manage';
const ATTENDANCE_READ = 'attendanceentries.read';

const eventStatusLabels: Record<EscoHrEventStatus, string> = {
  Received: 'Received',
  MappingRequired: 'Needs mapping',
  Rejected: 'Rejected',
};

/** Plain-language versions of the reason codes the server records. An unknown code is shown generically, never raw. */
const eventReasonLabels: Record<string, string> = {
  EmployeeNotMapped: 'The escoHR employee is not mapped to an EPN employee yet.',
  EnvelopeMismatch: 'The event did not match its own headers.',
  UnsupportedSchemaVersion: 'The event used a version EPN does not support.',
  UnsupportedEventType: 'The event type is not one EPN accepts.',
  InvalidOccurredAt: 'The event had no valid time.',
  PersonReferenceMissing: 'The event did not identify an employee.',
  MalformedJson: 'The event could not be read.',
};

/**
 * The escoHR Mobile Time & Attendance connection, signing secret, sync history and employee mapping
 * (docs/escohr-time-attendance-contract.md). Everything shown comes from the server: nothing is invented while the feature is locked,
 * not configured or unreachable, and a lookup that fails is shown as unavailable rather than as locked or configured. Permission and plan
 * checks here only hide or disable controls; the endpoints enforce both. The raw signing secret exists only in this component's state,
 * only until the admin confirms they saved it; it is never written to browser storage. The connection reference is shown separately and
 * does not change when the secret is rotated.
 */
export function EscoHrMobilePanel() {
  const can = usePermissionCheck();
  const canRead = can(INTEGRATIONS_READ);
  const canManage = can(INTEGRATIONS_MANAGE);
  const canSeeAttendance = can(ATTENDANCE_READ);

  const [status, setStatus] = useState<EscoHrStatusResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [events, setEvents] = useState<EscoHrEventResponse[]>([]);
  const [mappings, setMappings] = useState<EscoHrMappingResponse[]>([]);
  const [employees, setEmployees] = useState<EmployeeResponse[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [secret, setSecret] = useState<EscoHrSigningSecretResponse | null>(null);
  const [confirming, setConfirming] = useState<'rotate' | 'disconnect' | null>(null);

  const [escoHrRef, setEscoHrRef] = useState('');
  const [epnEmployeeId, setEpnEmployeeId] = useState('');

  const connection = status?.connection ?? null;
  const live = connection !== null && connection.status !== 'Revoked';

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoadError(null);
    try {
      const next = await timeAndLeaveGateway.getEscoHrStatus(signal);
      if (signal?.aborted) return;
      setStatus(next);

      if (next.connection && canRead) {
        const [history, mapped] = await Promise.all([
          timeAndLeaveGateway.listEscoHrEvents(50, signal),
          next.connection.status === 'Revoked' ? Promise.resolve([] as EscoHrMappingResponse[]) : timeAndLeaveGateway.listEscoHrMappings(signal),
        ]);
        if (signal?.aborted) return;
        setEvents(history);
        setMappings(mapped);
      } else {
        setEvents([]);
        setMappings([]);
      }
    } catch (err) {
      if (signal?.aborted) return;
      setLoadError(friendlyErrorMessage(err, 'Could not load the escoHR connection.'));
    }
  }, [canRead]);

  useEffect(() => {
    const controller = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const entitled = status?.entitlement === 'Entitled';
  const needEmployees = live && ((entitled && canManage) || canSeeAttendance);
  useEffect(() => {
    if (!needEmployees) return;
    let cancelled = false;
    (async () => {
      try {
        const all = await listAllEmployees(peopleGateway, { sortBy: 'name' });
        if (!cancelled) setEmployees(all.filter((e) => e.isActive));
      } catch {
        // The mapping form stays empty; the connection itself is unaffected and the next load retries.
      }
    })();
    return () => { cancelled = true; };
  }, [needEmployees]);

  async function run(action: () => Promise<void>, fallback: string) {
    setActionError(null);
    setBusy(true);
    try {
      await action();
      await load();
    } catch (err) {
      setActionError(friendlyErrorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  }

  const connect = () => run(async () => { await timeAndLeaveGateway.connectEscoHr(); }, 'Could not set up the escoHR connection.');

  const issueSecret = () => run(async () => {
    setConfirming(null);
    setSecret(await timeAndLeaveGateway.issueEscoHrSigningSecret());
  }, 'Could not generate the signing secret.');

  const disconnect = () => run(async () => {
    setConfirming(null);
    setSecret(null);
    await timeAndLeaveGateway.disconnectEscoHr();
  }, 'Could not disconnect escoHR.');

  const addMapping = (e: FormEvent) => {
    e.preventDefault();
    return run(async () => {
      await timeAndLeaveGateway.createEscoHrMapping({ escoHrEmployeeRef: escoHrRef.trim(), epnEmployeeId });
      setEscoHrRef('');
      setEpnEmployeeId('');
    }, 'Could not map the employee.');
  };

  const removeMapping = (id: string) => run(async () => { await timeAndLeaveGateway.removeEscoHrMapping(id); }, 'Could not remove the mapping.');

  const employeeName = (id: string) => {
    const e = employees.find((x) => x.id === id);
    return e ? `${e.firstName} ${e.lastName} (${e.employeeNumber})` : 'EPN employee';
  };

  if (!status && !loadError) {
    return <section className="card" data-testid="escohr-panel" aria-busy="true"><p className="muted">Loading escoHR Mobile Time &amp; Attendance…</p></section>;
  }

  if (loadError || !status) {
    return (
      <section className="card" data-testid="escohr-unavailable" aria-labelledby="escohr-heading">
        <h3 id="escohr-heading">escoHR Mobile Time &amp; Attendance</h3>
        <RetryableError message={loadError ?? 'Could not load the escoHR connection.'} onRetry={() => void load()} />
      </section>
    );
  }

  if (status.entitlement === 'Unknown') {
    return (
      <section className="card" data-testid="escohr-unavailable" aria-labelledby="escohr-heading">
        <h3 id="escohr-heading">escoHR Mobile Time &amp; Attendance</h3>
        <p className="muted">The status of this capability is temporarily unavailable. Nothing is shown as configured or locked until it can be confirmed.</p>
        <button type="button" onClick={() => void load()}>Try again</button>
      </section>
    );
  }

  const note = statusNote(status.displayStatus);

  const header = (
    <div className="section-header">
      <h3 id="escohr-heading">escoHR Mobile Time &amp; Attendance</h3>
      <span className={`badge ${status.displayStatus === 'Operational' ? '' : 'badge--caution'}`} data-testid="escohr-status-badge">
        {displayStatusLabels[status.displayStatus]}
      </span>
    </div>
  );

  const lockedBlock = status.entitlement === 'NotEntitled' && (
    <section className="access-restriction" role="region" aria-label={LOCKED_TITLE} data-testid="escohr-locked">
      <h3 className="access-restriction__title"><span aria-hidden="true">🔒</span> {LOCKED_TITLE}</h3>
      <p>{LOCKED_MESSAGE}</p>
      <p className="muted">{lockedReason(status.entitlementReason)}</p>
      {status.entitlementReason && PLAN_REASONS.has(status.entitlementReason) && (
        <Link className="access-restriction__cta" to="/settings/subscription">View Plans</Link>
      )}
    </section>
  );

  return (
    <section className="card" data-testid="escohr-panel" aria-labelledby="escohr-heading">
      {header}
      {lockedBlock}
      {note && <p data-testid="escohr-status-note">{note}</p>}
      {actionError && <p className="error" role="alert">{actionError}</p>}

      {secret && (
        <section className="access-restriction" role="region" aria-label="New signing secret" data-testid="escohr-secret">
          <h3 className="access-restriction__title">Signing secret (version {secret.version})</h3>
          <p>Give escoHR this secret now. <strong>It is shown only once</strong> and cannot be retrieved later; if it is lost, generate a new one. The connection reference is shown separately and does not change when the secret is rotated.</p>
          <p><code data-testid="escohr-secret-value">{secret.secret}</code></p>
          <button type="button" onClick={() => void navigator.clipboard?.writeText(secret.secret)}>Copy secret</button>{' '}
          <button type="button" onClick={() => setSecret(null)}>I have saved it</button>
        </section>
      )}

      {status.entitlement === 'Entitled' && !live && (
        <div data-testid="escohr-not-connected">
          <p>
            {connection?.status === 'Revoked'
              ? 'escoHR is disconnected. Mappings and the sync history are kept; setting up again starts a new connection with a new reference.'
              : 'The escoHR connection is not set up for this company.'}
          </p>
          {canManage
            ? <button type="button" disabled={busy} onClick={() => void connect()}>Set up connection</button>
            : <p className="muted">Only an administrator who manages integrations can set up the connection.</p>}
        </div>
      )}

      {connection && live && (
        <>
          <dl className="access-restriction__facts" data-testid="escohr-summary">
            <div><dt>Status</dt><dd>{displayStatusLabels[status.displayStatus]}</dd></div>
            <div><dt>Payroll processing</dt><dd>{status.payrollProcessingEnabled ? 'Enabled' : 'Not enabled'}</dd></div>
            <div><dt>Environment</dt><dd>{status.environment}</dd></div>
            {connection.connectionReference && (
              <div>
                <dt>Connection reference</dt>
                <dd>
                  <code data-testid="escohr-connection-reference">{connection.connectionReference}</code>{' '}
                  <button type="button" className="link" onClick={() => void navigator.clipboard?.writeText(connection.connectionReference ?? '')}>Copy</button>
                </dd>
              </div>
            )}
            <div><dt>Created</dt><dd>{formatWhen(connection.createdAtUtc)}</dd></div>
            <div><dt>First event received</dt><dd>{formatWhen(connection.activatedAtUtc)}</dd></div>
            <div><dt>Last successful sync</dt><dd>{formatWhen(connection.lastSuccessfulSyncAtUtc)}</dd></div>
            <div><dt>Last attempted sync</dt><dd>{formatWhen(connection.lastAttemptedSyncAtUtc)}</dd></div>
            <div><dt>Signing secret</dt><dd>{connection.signingSecretVersion > 0 ? `Version ${connection.signingSecretVersion}, last rotated ${formatWhen(connection.secretLastRotatedAtUtc)}` : 'Not issued yet'}</dd></div>
            <div><dt>Configuration version</dt><dd>{connection.configurationVersion}</dd></div>
            <div><dt>Mapped employees</dt><dd>{status.confirmedMappings}</dd></div>
            <div><dt>Events needing a mapping</dt><dd>{status.mappingRequiredEvents}</dd></div>
          </dl>
          {status.displayStatus === 'Suspended' && (
            <p className="muted">The company is no longer entitled to this capability. History stays readable and you can disconnect.</p>
          )}

          {canManage && (
            <p>
              {entitled && (
                connection.signingSecretVersion > 0
                  ? <button type="button" disabled={busy} onClick={() => setConfirming('rotate')}>Rotate signing secret</button>
                  : <button type="button" disabled={busy} onClick={() => void issueSecret()}>Generate signing secret</button>
              )}{' '}
              <button type="button" className="link" disabled={busy} onClick={() => setConfirming('disconnect')}>Disconnect</button>
            </p>
          )}
          {confirming === 'rotate' && (
            <div role="alert" className="inline-form">
              <strong>Rotating stops the current secret working immediately. escoHR must be given the new one at once. The connection reference does not change.</strong>
              <button type="button" disabled={busy} onClick={() => void issueSecret()}>Rotate now</button>
              <button type="button" className="link" onClick={() => setConfirming(null)}>Cancel</button>
            </div>
          )}
          {confirming === 'disconnect' && (
            <div role="alert" className="inline-form">
              <strong>Disconnect escoHR? No more events are accepted. Mappings and history are kept.</strong>
              <button type="button" disabled={busy} onClick={() => void disconnect()}>Disconnect now</button>
              <button type="button" className="link" onClick={() => setConfirming(null)}>Cancel</button>
            </div>
          )}
        </>
      )}

      {connection && !canRead && <p className="muted">You do not have permission to view the sync history or employee mappings.</p>}

      {connection && canRead && (
        <>
          <h4 style={{ marginTop: 16 }}>Sync history</h4>
          <p className="muted">Events are recorded as they arrive. Punches and timecards from escoHR are not shown or used in payroll yet.</p>
          <table aria-label="escoHR sync history">
            <thead>
              <tr><th>Received</th><th>Event</th><th>escoHR employee</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id}>
                  <td>{formatWhen(ev.receivedAtUtc)}</td>
                  <td>{ev.eventType}{ev.duplicateCount > 0 && <div className="muted">Repeated {ev.duplicateCount}×, ignored</div>}</td>
                  <td>{ev.escoHrEmployeeRef ?? '—'}</td>
                  <td>
                    {eventStatusLabels[ev.status] ?? ev.status}
                    {ev.statusReason && <div className="muted">{eventReasonLabels[ev.statusReason] ?? 'See the escoHR connection log.'}</div>}
                  </td>
                  <td>
                    {live && canManage && entitled && ev.status === 'MappingRequired' && ev.escoHrEmployeeRef && (
                      <button type="button" className="link" onClick={() => setEscoHrRef(ev.escoHrEmployeeRef ?? '')}>Map</button>
                    )}
                  </td>
                </tr>
              ))}
              {events.length === 0 && <tr><td colSpan={5} className="muted">No events received yet.</td></tr>}
            </tbody>
          </table>
        </>
      )}

      {connection && live && canRead && (
        <>
          <h4 style={{ marginTop: 16 }}>Employee mapping</h4>
          <p className="muted">An escoHR employee is linked to an EPN employee of this company only, by an administrator. Names, emails and phone numbers are never matched automatically.</p>
          <table aria-label="escoHR employee mappings">
            <thead>
              <tr><th>escoHR employee</th><th>EPN employee</th><th>Confirmed</th><th></th></tr>
            </thead>
            <tbody>
              {mappings.map((m) => (
                <tr key={m.id}>
                  <td>{m.escoHrEmployeeRef}</td>
                  <td>{employeeName(m.epnEmployeeId)}</td>
                  <td className="muted">{formatWhen(m.confirmedAtUtc)}</td>
                  <td>{canManage && <button type="button" className="link" disabled={busy} onClick={() => void removeMapping(m.id)}>Remove</button>}</td>
                </tr>
              ))}
              {mappings.length === 0 && <tr><td colSpan={4} className="muted">No employees are mapped yet.</td></tr>}
            </tbody>
          </table>

          {canManage && entitled && (
            <form className="inline-form" onSubmit={(e) => void addMapping(e)} style={{ marginTop: 12 }}>
              <input aria-label="escoHR employee reference" placeholder="escoHR employee reference" value={escoHrRef} onChange={(e) => setEscoHrRef(e.target.value)} required />
              <select aria-label="EPN employee" value={epnEmployeeId} onChange={(e) => setEpnEmployeeId(e.target.value)} required>
                <option value="">Choose an employee…</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName} ({e.employeeNumber})</option>)}
              </select>
              <button type="submit" disabled={busy || !escoHrRef.trim() || !epnEmployeeId}>Confirm mapping</button>
            </form>
          )}
        </>
      )}

      {connection && canSeeAttendance && (
        <EscoHrAttendanceSection
          status={status}
          canManage={canManage}
          entitled={entitled}
          employeeName={employeeName}
          onChanged={() => void load()}
        />
      )}
    </section>
  );
}
